import { useCallback, useEffect, useState } from 'react'

import { useHotelPricingConfig } from '@/shared/hooks/use-hotel-pricing-config'
import {
    AdditionalService,
    DriverService,
    DriverServiceType,
    HairdressingService,
    HairdressingServiceType,
    MedicationService,
    SpecialCareService,
    SpecialFoodService,
    SpecialFoodType,
} from '@monorepo/functions/src/types/services'

type ServicesState = {
    services: AdditionalService[]
}

const areServicesEqual = (a: AdditionalService[], b: AdditionalService[]) => {
    if (a.length !== b.length) return false
    return JSON.stringify(a) === JSON.stringify(b)
}

export const useServices = (
    initialServices: AdditionalService[] = [],
    onServiceChange: (services: AdditionalService[]) => void,
) => {
    const [state, setState] = useState<ServicesState>({ services: initialServices })
    const { config } = useHotelPricingConfig()

    const setServices = useCallback(
        (params: ServicesState | ((prevState: ServicesState) => ServicesState)) => {
            if (typeof params === 'function') {
                setState(prevState => {
                    const newState = params(prevState)
                    if (!areServicesEqual(prevState.services, newState.services)) {
                        onServiceChange(newState.services)
                    }
                    return newState
                })
            } else {
                if (!areServicesEqual(state.services, params.services)) {
                    setState(params)
                    onServiceChange(params.services)
                }
            }
        },
        [onServiceChange, state.services],
    )

    useEffect(() => {
        if (!areServicesEqual(state.services, initialServices)) {
            setState({ services: initialServices })
        }
    }, [initialServices])

    const addOrUpdateDriverService = useCallback(
        (serviceType: DriverServiceType, pickupTime?: string, dropoffTime?: string, isOutOfHours?: boolean) => {
            setServices(current => {
                const services = current.services.filter(s => s.type !== 'driver')
                const driverService: DriverService = {
                    type: 'driver',
                    serviceType,
                    pickupTime,
                    dropoffTime,
                    isOutOfHours,
                }
                return { services: [...services, driverService] }
            })
        },
        [config],
    )

    const removeDriverService = useCallback(() => {
        setServices(current => ({
            services: current.services.filter(s => s.type !== 'driver'),
        }))
    }, [])

    const addOrUpdateSpecialFoodService = useCallback(
        (petIndex: number, foodType: SpecialFoodType) => {
            setServices(current => {
                const services = current.services.filter(s => !(s.type === 'special_food' && s.petIndex === petIndex))
                const foodService: SpecialFoodService = {
                    type: 'special_food',
                    petIndex,
                    foodType,
                    price: config?.specialFood.price,
                }
                return { services: [...services, foodService] }
            })
        },
        [config],
    )

    const removeSpecialFoodService = useCallback((petIndex: number) => {
        setServices(current => ({
            services: current.services.filter(s => !(s.type === 'special_food' && s.petIndex === petIndex)),
        }))
    }, [])

    const addOrUpdateMedicationService = useCallback(
        (petIndex: number, comment: string = '', frequency: 'single' | 'multiple' = 'single') => {
            setServices(current => {
                const services = current.services.filter(s => !(s.type === 'medication' && s.petIndex === petIndex))
                let price = 0

                if (config) {
                    price = frequency === 'multiple' ? config.medication.multiple.price : config.medication.once.price
                }

                const medicationService: MedicationService = {
                    type: 'medication',
                    petIndex,
                    comment: comment || '',
                    frequency,
                    price,
                }
                console.log('medicationService', medicationService)
                console.log('services', services)
                return { services: [...services, medicationService] }
            })
        },
        [config],
    )

    const removeMedicationService = useCallback((petIndex: number) => {
        setServices(current => ({
            services: current.services.filter(s => !(s.type === 'medication' && s.petIndex === petIndex)),
        }))
    }, [])

    const addOrUpdateSpecialCareService = useCallback(
        (petIndex: number, comment?: string) => {
            setServices(current => {
                const services = current.services.filter(s => !(s.type === 'special_care' && s.petIndex === petIndex))
                const specialCareService: SpecialCareService = {
                    type: 'special_care',
                    petIndex,
                    comment: comment || '',
                    price: config?.healing.price,
                }
                return { services: [...services, specialCareService] }
            })
        },
        [config],
    )

    const removeSpecialCareService = useCallback((petIndex: number) => {
        setServices(current => ({
            services: current.services.filter(s => !(s.type === 'special_care' && s.petIndex === petIndex)),
        }))
    }, [])

    const addOrUpdateHairdressingService = useCallback((petIndex: number, services: HairdressingServiceType[]) => {
        setServices(current => {
            const currentHairdressingService = current.services.find(s => s.type === 'hairdressing' && s.petIndex === petIndex)
            const currentServices = current.services.filter(
                s => !(s.type === 'hairdressing' && s.petIndex === petIndex),
            )
            const hairdressingService: HairdressingService = {
                type: 'hairdressing',
                petIndex,
                services,
                // Hairdressing prices are determined later with the hairdresser
                price: currentHairdressingService?.price || 0,
            }
            return { services: [...currentServices, hairdressingService] }
        })
    }, [])

    const removeHairdressingService = useCallback((petIndex: number) => {
        setServices(current => ({
            services: current.services.filter(s => !(s.type === 'hairdressing' && s.petIndex === petIndex)),
        }))
    }, [])

    // Helper functions to get services
    const getDriverService = useCallback(
        () => state.services.find(s => s.type === 'driver') as DriverService | undefined,
        [state.services],
    )

    const getSpecialFoodService = useCallback(
        (petIndex: number) =>
            state.services.find(s => s.type === 'special_food' && s.petIndex === petIndex) as
                | SpecialFoodService
                | undefined,
        [state.services],
    )

    const getMedicationService = useCallback(
        (petIndex: number) =>
            state.services.find(s => s.type === 'medication' && s.petIndex === petIndex) as
                | MedicationService
                | undefined,
        [state.services],
    )

    const getSpecialCareService = useCallback(
        (petIndex: number) =>
            state.services.find(s => s.type === 'special_care' && s.petIndex === petIndex) as
                | SpecialCareService
                | undefined,
        [state.services],
    )

    const getHairdressingService = useCallback(
        (petIndex: number) =>
            state.services.find(s => s.type === 'hairdressing' && s.petIndex === petIndex) as
                | HairdressingService
                | undefined,
        [state.services],
    )

    return {
        services: state.services,
        addOrUpdateDriverService,
        removeDriverService,
        addOrUpdateSpecialFoodService,
        removeSpecialFoodService,
        addOrUpdateMedicationService,
        removeMedicationService,
        addOrUpdateSpecialCareService,
        removeSpecialCareService,
        addOrUpdateHairdressingService,
        removeHairdressingService,
        getDriverService,
        getSpecialFoodService,
        getMedicationService,
        getSpecialCareService,
        getHairdressingService,
    }
}
